
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Check, X, ExternalLink } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';

interface PartnerApplication {
  id: string;
  full_name: string;
  email: string;
  website: string | null;
  target_audience: string;
  motivation: string;
  status: 'pending' | 'accepted' | 'rejected';
  created_at: string;
}

const AdminPartnerApplicationsTab = () => {
  const [applications, setApplications] = useState<PartnerApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState<PartnerApplication | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { toast } = useToast();

  const fetchApplications = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('partner_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;
      setApplications(data as PartnerApplication[]);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast({
        title: 'Fehler',
        description: 'Beim Laden der Partner-Bewerbungen ist ein Fehler aufgetreten.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [statusFilter]);

  const updateApplicationStatus = async (id: string, status: 'pending' | 'accepted' | 'rejected') => {
    try {
      const { error } = await supabase
        .from('partner_applications')
        .update({ status })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      setApplications(prev => prev.map(app => app.id === id ? { ...app, status } : app));
      
      // Update selected application if it's open
      if (selectedApplication?.id === id) {
        setSelectedApplication({ ...selectedApplication, status });
      }

      toast({
        title: 'Status aktualisiert',
        description: `Die Bewerbung wurde als "${status}" markiert.`,
      });
    } catch (error) {
      console.error('Error updating application status:', error);
      toast({
        title: 'Fehler',
        description: 'Beim Aktualisieren des Status ist ein Fehler aufgetreten.',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Ausstehend</Badge>;
      case 'accepted':
        return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">Angenommen</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">Abgelehnt</Badge>;
      default:
        return <Badge variant="outline">Unbekannt</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Partner-Bewerbungen</CardTitle>
        <CardDescription>
          Hier werden alle Bewerbungen für das Partner-Programm aufgelistet.
        </CardDescription>

        <div className="flex items-center gap-4 mt-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Status Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Bewerbungen</SelectItem>
              <SelectItem value="pending">Ausstehend</SelectItem>
              <SelectItem value="accepted">Angenommen</SelectItem>
              <SelectItem value="rejected">Abgelehnt</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={fetchApplications} className="ml-auto">
            Aktualisieren
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="w-6 h-6 animate-spin text-chrona-accent" />
          </div>
        ) : applications.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            <p>Keine Partner-Bewerbungen gefunden.</p>
          </div>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {applications.map((application) => (
                  <TableRow key={application.id}>
                    <TableCell className="font-medium">{application.full_name}</TableCell>
                    <TableCell>{application.email}</TableCell>
                    <TableCell>{formatDate(application.created_at)}</TableCell>
                    <TableCell>{getStatusBadge(application.status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            setSelectedApplication(application);
                            setDetailsOpen(true);
                          }}
                        >
                          Details
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Application Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Partner-Bewerbung Details</DialogTitle>
            <DialogDescription>
              Alle Details zur Bewerbung von {selectedApplication?.full_name}
            </DialogDescription>
          </DialogHeader>

          {selectedApplication && (
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-4">
                <div className="grid grid-cols-[100px_1fr] gap-2">
                  <div className="font-medium text-gray-400">Name:</div>
                  <div>{selectedApplication.full_name}</div>
                  
                  <div className="font-medium text-gray-400">E-Mail:</div>
                  <div>{selectedApplication.email}</div>
                  
                  <div className="font-medium text-gray-400">Website:</div>
                  <div>
                    {selectedApplication.website ? (
                      <a 
                        href={selectedApplication.website.startsWith('http') ? selectedApplication.website : `https://${selectedApplication.website}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-chrona-accent hover:underline flex items-center gap-1"
                      >
                        {selectedApplication.website}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </div>
                  
                  <div className="font-medium text-gray-400">Status:</div>
                  <div>{getStatusBadge(selectedApplication.status)}</div>
                  
                  <div className="font-medium text-gray-400">Datum:</div>
                  <div>{formatDate(selectedApplication.created_at)}</div>
                </div>
                
                <div>
                  <div className="font-medium text-gray-400 mb-1">Zielgruppe:</div>
                  <div className="bg-gray-800/50 p-3 rounded-md">
                    {selectedApplication.target_audience}
                  </div>
                </div>
                
                <div>
                  <div className="font-medium text-gray-400 mb-1">Motivation:</div>
                  <div className="bg-gray-800/50 p-3 rounded-md whitespace-pre-line">
                    {selectedApplication.motivation}
                  </div>
                </div>
              </div>
              
              <div className="mt-6 border-t border-gray-800 pt-4">
                <h4 className="font-medium mb-2">Status ändern:</h4>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => updateApplicationStatus(selectedApplication.id, 'accepted')}
                    variant={selectedApplication.status === 'accepted' ? 'default' : 'outline'}
                    className={selectedApplication.status === 'accepted' ? 'bg-green-600 hover:bg-green-700' : ''}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Annehmen
                  </Button>
                  <Button 
                    onClick={() => updateApplicationStatus(selectedApplication.id, 'rejected')}
                    variant={selectedApplication.status === 'rejected' ? 'destructive' : 'outline'}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Ablehnen
                  </Button>
                  <Button 
                    onClick={() => updateApplicationStatus(selectedApplication.id, 'pending')}
                    variant={selectedApplication.status === 'pending' ? 'default' : 'outline'}
                    className={selectedApplication.status === 'pending' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
                  >
                    Ausstehend
                  </Button>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminPartnerApplicationsTab;
